import React from "react";
import { Box } from "@mui/material";
import { NumericFormat } from "react-number-format";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import {
  StockItem,
  StockItemGroupStats,
  ProductAvailabilityCode,
  productAvailability,
} from "../models/models";
import { productCondition } from "../../../common/models/CommonModels";
import ImageBox from "../../../shared/components/ImageBox";
import { dt } from "../../../shared/themes/redesignTokens";

// ═══════════════════════════════════════════════════════════════
//  DESIGN TOKENS (subset)
// ═══════════════════════════════════════════════════════════════

const groupBadgeColors = {
  forSale: { bg: "#EAFAF1", text: "#27AE60" },
  internal: { bg: "#F4F6F9", text: "#6B7280" },
};

const availabilityBadgeColors: Record<number, { bg: string; text: string }> = {
  [ProductAvailabilityCode.inStock]: { bg: "#EAFAF1", text: "#27AE60" },
  [ProductAvailabilityCode.in7Days]: { bg: "#FEF9E7", text: "#F39C12" },
  [ProductAvailabilityCode.in14Days]: { bg: "#FEF9E7", text: "#E67E22" },
  [ProductAvailabilityCode.in1Month]: { bg: "#F9EBEA", text: "#C0392B" },
};

// ═══════════════════════════════════════════════════════════════

interface MobileStockItemCardProps {
  item: StockItem;
  groups: StockItemGroupStats[];
  index: number;
  tInventory: (key: string, options?: any) => string;
  tCommon: (key: string, options?: any) => string;
  onClick: (item: StockItem) => void;
}

const MobileStockItemCard = ({ item, groups, index, tInventory, tCommon, onClick }: MobileStockItemCardProps) => {
  const group = groups.find((g) => g.id === item.groupId);
  const isForSale = group ? !group.isPrivate : true;
  const gc = isForSale ? groupBadgeColors.forSale : groupBadgeColors.internal;
  const ac = availabilityBadgeColors[item.availabilityCode] || availabilityBadgeColors[ProductAvailabilityCode.inStock];

  return (
    <Box
      onClick={() => onClick(item)}
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.cardBorder}`,
        borderLeft: `3px solid ${gc.text}`,
        padding: "12px",
        cursor: "pointer",
        boxShadow: dt.cardShadow,
        overflow: "hidden",
        width: "100%",
        boxSizing: "border-box",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
        "@keyframes cardReveal": {
          "0%": { opacity: 0, transform: "translateY(16px) scale(0.98)" },
          "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
        },
        "&:hover": {
          boxShadow: dt.cardShadowHover,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Header: Image + Product number + Group badge */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 0.5, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flex: 1 }}>
          <ImageBox size="small" src={item.file?.content} />
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{
              color: dt.textLink,
              fontWeight: 600,
              fontSize: "12px",
              fontFamily: dt.fontMono,
              letterSpacing: "0.3px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {item.product?.number}
            </Box>
            {item.alternativeNumber && (
              <Box sx={{
                color: dt.textMuted,
                fontSize: "11px",
                fontFamily: dt.fontMono,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {item.alternativeNumber}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{
          display: "inline-flex",
          alignItems: "center",
          backgroundColor: gc.bg,
          borderLeft: `3px solid ${gc.text}`,
          borderRadius: "0 8px 8px 0",
          padding: "3px 10px 3px 6px",
          fontSize: "11px",
          fontWeight: 600,
          fontFamily: dt.font,
          color: gc.text,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}>
          {isForSale ? tInventory("forSale") : tInventory("internalStock")}
        </Box>
      </Box>

      {/* Body: Brand + Category + Condition */}
      <Box sx={{ mb: 1, overflow: "hidden" }}>
        <Box sx={{
          color: dt.textPrimary,
          fontWeight: 500,
          fontSize: "13px",
          fontFamily: dt.font,
          mb: 0.3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textTransform: "uppercase",
        }}>
          {item.product?.manufacturer?.name}
        </Box>
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          color: dt.textSecondary,
          fontSize: "12px",
          overflow: "hidden",
        }}>
          {item.product?.category?.name && (
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {item.product.category.name}
            </Box>
          )}
          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
            {tCommon(`productCondition.${productCondition(item?.conditionCode)}`)}
          </Box>
        </Box>
      </Box>

      {/* Footer: Availability + Price + Qty + Chevron */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        pt: 1,
        borderTop: `1px solid ${dt.rowBorder}`,
        gap: 0.5,
      }}>
        <Box sx={{
          display: "inline-flex",
          alignItems: "center",
          backgroundColor: ac.bg,
          borderLeft: `3px solid ${ac.text}`,
          borderRadius: "0 8px 8px 0",
          padding: "3px 10px 3px 6px",
          fontSize: "11px",
          fontWeight: 600,
          fontFamily: dt.font,
          color: ac.text,
          whiteSpace: "nowrap",
        }}>
          {productAvailability(item.availabilityCode, tInventory)}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {item.price?.value != null && (
            <Box sx={{
              fontSize: "12px",
              fontWeight: 700,
              fontFamily: dt.fontMono,
              color: dt.textPrimary,
              whiteSpace: "nowrap",
            }}>
              <NumericFormat value={item.price.value} displayType="text" thousandSeparator=" " suffix=" EUR" />
            </Box>
          )}
          {item.quantity?.value != null && (
            <Box sx={{
              fontSize: "11px",
              fontFamily: dt.fontMono,
              color: dt.textMuted,
              whiteSpace: "nowrap",
            }}>
              x{item.quantity.value}
            </Box>
          )}
          <ChevronRightIcon sx={{ color: dt.textMuted, fontSize: "18px" }} />
        </Box>
      </Box>
    </Box>
  );
};

export default MobileStockItemCard;
